import { useEffect, useState } from 'react';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { useApolloErrorHandler } from 'hooks';
import { GET_COUNTRIES, GetCountriesOutput } from 'api/graphql/country';
import capitalizeEveryWord from '../../../../../shared/stringUtils/capitalizeEveryWord';
import { TXT_COUNTRY } from '../../../../../shared/translations';

export interface CountrySelectorProps {
  label?: string;
  country?: string;
  error?: boolean;
  onSelectedChange: (country: string | null) => void;
  disabled?: boolean;
}

export const CountrySelector = (props: CountrySelectorProps) => {
  const { t } = useTranslation();
  // const alpha2Codes = getCountryAplha2Codes();
  const [selected, setSelected] = useState<string | null>(props.country || null);
  const [options, setOptions] = useState<string[]>([]);
  const { apolloErrorHandler } = useApolloErrorHandler();

  const { loading } = useQuery<GetCountriesOutput>(GET_COUNTRIES, {
    fetchPolicy: 'network-only',
    skip: !!props.disabled,
    onError: apolloErrorHandler,
    onCompleted: (data) => {
      const countries = [...data.countries];
      if (selected && !countries.some(() => selected)) {
        countries.push(selected);
      }
      setOptions(countries);
    }
  });

  const onSelectedChange = (_: object, value: string | null) => {
    setSelected(() => value);
    props.onSelectedChange(value);
  };

  useEffect(() => {
    setSelected(() => props.country || null);
  }, [props.country]);

  return (
    <Autocomplete
      id="country-selector"
      onChange={onSelectedChange}
      value={selected}
      options={options}
      fullWidth
      disabled={props.disabled}
      loading={loading}
      renderInput={(params) => (
        <TextField
          {...params}
          label={props.label === '' ? undefined : capitalizeEveryWord(props.label || t(TXT_COUNTRY))}
          variant="outlined"
          error={props.error}
        />
      )}
    />
  );
};
